// HelpChat.jsx
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Footer from "../../Component/Footer/Footer";

import { call } from "../../assets/img";
import SubmitButton from "../../Component/SubmitButton";
import { get, post } from "../../Services/ApiHandler";
import { URLS } from "../../Services/Constant";
import CustomScroll from "../../Component/CustomScroll";
import useStore from "../redux/store/useStore";
import RawWebSocket from "../../Websocker/RawWebSocket";
import { MaskIcon } from "../../Component/MaskIcon";
import { PRIMARY_COLOR } from "../../Services/Colors";

const HelpChat = () => {
  const location = useLocation();

  // ── Detect if this is a new chat initiated by admin ──
  const isNewChat = location?.state?.isNewChat || false;
  const chatId = location?.state?.chatId || "";
  const mobile = location?.state?.mobile || "";
  const clientName = location?.state?.client_name || "";

  // ── New Chat state ──
  const [newMobile, setNewMobile] = useState("");
  const [mobileError, setMobileError] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // ── Chat state ──
  const [messages, setMessages] = useState(new Map());
  const messageLoaded = useRef(false);
  const messagesRef = useRef(new Map());
  const [msg, setMsg] = useState("");
  const [loader, setLoader] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  // ── UI state ──
  const [browsHeader, setBrowsHeader] = useState(true);
  const [footerSpace, setFooterSpace] = useState(100);

  // ── Refs ──
  const scrollRef = useRef(null);
  const footerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchDebounce = useRef(null);
  const maxHeight = useRef(window.visualViewport?.height || window.innerHeight);
  const isFirstRun = useRef(true);
  const replyUnsubscribeRef = useRef(null);

  const { user } = useStore();
  
  // ── Load chat history + websocket (existing chats only) ──
  useEffect(() => {
    // Releases the new-chat reply listener + active-chat marker set by
    // sendMessage(). Runs whenever chatId/isNewChat changes (e.g. the
    // admin opens a different chat without leaving this route) and on
    // unmount, so the marker never outlives the chat it belongs to.
    const releaseNewChatState = () => {
      replyUnsubscribeRef.current?.();
      replyUnsubscribeRef.current = null;
      RawWebSocket.clearActiveChat();
    };

    if (!chatId || isNewChat) return releaseNewChatState;

    // Mark this client's chat as "open" so the global notification
    // popup (App.js) skips it — we're already looking at the messages.
    RawWebSocket.setActiveChat(chatId);

    const unsubscribe = RawWebSocket.listen("chat_message", (e) => {
      let d = e?.data;
      if (messageLoaded.current) {
        storeMessages([d]);
        setTimeout(scrollToBottom, 100);
      } else {
        messagesRef.current.set(d?.id, d);
      }
    });

    const fetchHistory = async () => {
      setHistoryLoading(true);
      const res = await post(URLS.CHATHISTORY, { client_id: chatId });
      setHistoryLoading(false);
      if (res?.status === "success") {
        storeMessages(res?.data);
        setTimeout(scrollToBottom, 100);
      }
    };

    fetchHistory();

    return () => {
      unsubscribe();
      RawWebSocket.clearActiveChat();
    };
  }, [chatId, isNewChat]);

  // ── Click outside dropdown ──
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Keyboard resize (mobile browser) ──
  useEffect(() => {
    const handleResize = () => {
      const visibleHeight = window.visualViewport?.height || window.innerHeight;
      if (isFirstRun.current) {
        isFirstRun.current = false;
        setBrowsHeader(true);
        return;
      }
      if (visibleHeight < maxHeight.current) {
        setBrowsHeader(true);
      } else {
        maxHeight.current = visibleHeight;
        setBrowsHeader(false);
      }
    };
    window.visualViewport?.addEventListener("resize", handleResize);
    return () => window.visualViewport?.removeEventListener("resize", handleResize);
  }, [browsHeader]);

  // ── Footer height tracking ──
  useEffect(() => {
    const updateFooterSpace = () => {
      const footerHeight = footerRef.current?.offsetHeight || 0;
      setFooterSpace(footerHeight + 20);
    };
    updateFooterSpace();
    window.addEventListener("resize", updateFooterSpace);
    window.visualViewport?.addEventListener("resize", updateFooterSpace);
    return () => {
      window.removeEventListener("resize", updateFooterSpace);
      window.visualViewport?.removeEventListener("resize", updateFooterSpace);
    };
  }, []);

  // ── Auto scroll on new messages ──
  useEffect(() => {
    const rafId = window.requestAnimationFrame(scrollToBottom);
    return () => window.cancelAnimationFrame(rafId);
  }, [messages.size, footerSpace]);

  // ── Helpers ──
  const storeMessages = (data) => {
    let receivedMessages = messageLoaded.current
      ? []
      : Array.from(messagesRef.current.values());
    setMessages((prev) => {
      let mapped = new Map(prev);
      [...receivedMessages, ...data].forEach((item) => mapped.set(item?.id, item));
      return mapped;
    });
    messageLoaded.current = true;
  };

  const scrollToBottom = () => {
    try {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    } catch { }
  };

  // ── Search handler (debounced 300ms) ──
  const handleMobileSearch = (value) => {
    setNewMobile(value);
    setSelectedUser(null);
    if (mobileError) setMobileError("");

    if (searchDebounce.current) clearTimeout(searchDebounce.current);

    if (!value.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    searchDebounce.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await get(
          `${URLS.CLIENTSEARCH}?search=${encodeURIComponent(value.trim())}`
        );
        if (res?.status === "success" && Array.isArray(res?.data)) {
          setSearchResults(res.data);
          setShowDropdown(res.data.length > 0);
        } else {
          setSearchResults([]);
          setShowDropdown(false);
        }
      } catch {
        setSearchResults([]);
        setShowDropdown(false);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  };

  // ── Select user from dropdown ──
  const handleSelectUser = (user) => {
    setNewMobile(user.name); // show name in input
    setSelectedUser(user);   // mobile stored here for payload
    setShowDropdown(false);
    setSearchResults([]);
  };

  // ── Send message ──
  const sendMessage = async () => {
    if (!msg.trim()) return;

    if (isNewChat) {
      if (!newMobile.trim()) {
        setMobileError("Please search and select a user.");
        return;
      }
      setMobileError("");
    }

    setLoader(true);

    const body = isNewChat
      ? {
        mobile: selectedUser?.mobile,
        message: msg,
      }
      : {
        client_id: chatId,
        message: msg,
      };

    try {
      const res = await post(isNewChat ? URLS.CHATNEW : URLS.CHATSEND, body);

      if (res?.status === "success") {
        setMsg("");

        const history = res?.data?.chat_history || [];
        const sentMessage = res?.data?.message_sent?.data;
        const clientId = res?.data?.client?.client_id;

        const allMessages = sentMessage
          ? [...history.filter((m) => m.id !== sentMessage.id), sentMessage]
          : history;

        if (allMessages.length > 0) {
          storeMessages(allMessages);
          setTimeout(scrollToBottom, 100);
        }

        // Start listening for replies on this client's channel
        if (clientId) {
          RawWebSocket.setActiveChat(clientId);
          replyUnsubscribeRef.current?.();
          replyUnsubscribeRef.current = RawWebSocket.listen("chat_message", (e) => {
            const d = e?.data;
            if (d?.client_id === clientId) {
              storeMessages([d]);
              setTimeout(scrollToBottom, 100);
            }
          });
        }
      }
    } catch {
      // handle silently
    } finally {
      setLoader(false);
    }
  };

  // ── Message Card ──
  const MessageCard = ({ data }) => {
    const isRight = Boolean(data.is_admin_sender);
    return (
      <div className={`col-md-12 mt-2 ${isRight ? "ac-je" : "ac-js"}`}>
        <div
          className="alignCCC"
          style={{
            alignItems: isRight ? "flex-end" : "flex-start",
            textAlign: isRight ? "right" : "left",
          }}
        >
          <p className="f3 mb-1 primary1">
            {isRight ? data?.admin_name : "User"}
          </p>
          <p
            style={{
              resize: "none",
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
              maxWidth: "90%",
            }}
            className={`${isRight ? "User_box" : "Admin_box"} mb-1`}
          >
            {data.message}
          </p>
          <p className="f1 primary1">{data?.created_at || data?.time}</p>
        </div>
      </div>
    );
  };

  // ── Render ──
  return (
    <div className="dashRightView">
      <CustomScroll>
        <div className="container_overalls">
          <form className="col-md-12">
            {/* CHAT AREA */}
            <div className="col-md-5">
              <div
                ref={scrollRef}
                className="user-scroll"
                style={{
                  maxHeight: 420,
                  overflowY: "auto",
                  width: "100%",
                  paddingBottom: footerSpace,
                }}
              >
                {isNewChat ? (
                  /* ── NEW CHAT: Search + dropdown ── */
                  <div
                    ref={dropdownRef}
                    className="form-group text-start my-3"
                    style={{ position: "relative" }}
                  >
                    <label className="form-label primary1 m-0 f3">
                      Select Client <span style={{ color: PRIMARY_COLOR }}>*</span>
                    </label>

                    <div className="login-input1 mt-1" style={{ display: "flex" }}>
                      <input
                        className="f3"
                        value={newMobile}
                        onChange={(e) => handleMobileSearch(e.target.value)}
                        placeholder="Search by name or mobile..."
                        autoComplete="off"
                        style={{ flex: 1 }}
                      />
                      {searchLoading ? (
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          style={{ width: 16, height: 16, margin: "auto 12px" }}
                        />
                      ) : (
                        <MaskIcon
                          src={call}
                          alt="call"
                          style={{ width: 20, marginRight: 12 }}
                        />
                      )}
                    </div>


                    {/* Validation error */}
                    {mobileError && (
                      <p style={{ color: "red", fontSize: 12, marginTop: 4 }} className="f2">
                        {mobileError}
                      </p>
                    )}

                    {/* Dropdown */}
                    {showDropdown && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          background: "#fff",
                          border: "1px solid #ddd",
                          borderRadius: 8,
                          zIndex: 999,
                          maxHeight: 200,
                          overflowY: "auto",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        }}
                      >
                        {searchResults.map((u, idx) => (
  <div
    key={u?.client_id || u?.id || idx}
    onClick={() => handleSelectUser(u)}
    style={{
      padding: "10px 14px",
      cursor: "pointer",
      borderBottom:
        idx < searchResults.length - 1
          ? "1px solid #f0f0f0"
          : "none",
    }}
    onMouseEnter={(e) =>
      (e.currentTarget.style.background = "#f8f0f4")
    }
    onMouseLeave={(e) =>
      (e.currentTarget.style.background = "#fff")
    }
  >
    <p className="f3 m-0 primary1" style={{ fontSize: 14 }}>  {/* ✅ add primary1 */}
      {u.name}
    </p>
    <p className="f2 m-0 primary1" style={{ fontSize: 12 }}>  {/* ✅ add primary1, remove color:#888 */}
      {u.mobile}
    </p>
  </div>
))}
                      </div>
                    )}
                  </div>
                ) : (
                  /* ── EXISTING CHAT: Read-only user info ── */
                  <>
                    <div className="">
                      <label className="form-label primary1 m-0 f3">User Name</label>
                      <input
                        className="input-box f3"
                        style={{ color: "#565353" }}
                        value={clientName}
                        disabled
                      />
                    </div>

                    <div className="form-group text-start my-3">
                      <label className="form-label primary1 m-0 f3">Mobile No</label>
                      <div className="login-input1 mt-1" style={{ display: "flex" }}>
                        <input
                          className="f3"
                          value={mobile}
                          disabled
                          style={{ flex: 1 }}
                        />
                        <MaskIcon
                          src={call}
                          alt="call"
                          style={{ width: 20, marginRight: 12 }}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Messages */}
                {Array.from(messages.values()).map((m) => (
                  <MessageCard key={m.id} data={m} />
                ))}
              </div>
            </div>

            {/* FOOTER: message input + send */}
            <div ref={footerRef} className="chat-footer-fix">
              <div className="form-group col-md-5 text-start my-3">
                <div className="login-input col-md-5 mt-3">
                  <input
                    className="input-box f3"
                    value={msg}
                    onChange={(e) => setMsg(e.target.value)}
                    placeholder="Type..."
                  />
                </div>
              </div>

              <div className="col-md-5 my-3">
                <SubmitButton
                  title={loader ? "Sending..." : isNewChat ? "Send" : "Reply"}
                  style={{ marginBottom: browsHeader ? 60 : null }}
                  disabled={!msg.trim() || loader}
                  onClick={sendMessage}
                />
              </div>
            </div>
          </form>

          <Footer />
        </div>
      </CustomScroll>
    </div>
  );
};

export default HelpChat;